(() => {
  window.__HIOOL_PCA192_BOOT__ = {
    loaded: true,
    loadedAt: Date.now(),
    src: document.currentScript?.src || null,
  };

  if (window.HIOOLVisualSearchPCA192) return;

  const EXECUTING_SCRIPT_SRC = (() => {
    const current = document.currentScript?.src;
    if (current) return current;
    const scripts = [...document.scripts];
    return scripts.find((s) => /hiool-visual-pca192\.js(?:\?|$)/.test(s.src || ''))?.src || '';
  })();

  const ORT_WEB_VERSION = '1.26.0-dev.20260416-b7804b056c';
  const DEFAULTS = {
    dinoModel: 'onnx-community/dinov3-vits16-pretrain-lvd1689m-ONNX',
    transformersModule: 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@4.2.0/+esm',
    wasmPath: `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ORT_WEB_VERSION}/dist/`,
    preferWebGPU: false,
    topK: 5,
    maxWeight: 0.7,
    centroidWeight: 0.3,
  };

  let config = { ...DEFAULTS };
  let hfModulePromise;
  let dinoExtractorPromise;
  let catalogPromise;
  let transformPromise;
  let lastError = null;
  let lastStage = 'idle';

  const runtime = {
    backend: null,
    webgpuAvailable: Boolean(navigator.gpu?.requestAdapter),
    webgpuAdapterAvailable: null,
    wasmConfigured: false,
    wasmPath: DEFAULTS.wasmPath,
    modelReady: false,
    lastModelError: null,
  };

  function stage(name, cb) {
    lastStage = name;
    cb?.(name);
  }

  function scriptAssetUrl(filename) {
    if (!EXECUTING_SCRIPT_SRC) return null;
    try {
      return new URL(filename, EXECUTING_SCRIPT_SRC).href;
    } catch {
      return null;
    }
  }

  function resolvedManifestUrl() {
    return config.manifestUrl || scriptAssetUrl('hiool-pca192-manifest.json');
  }

  function resolvedTransformUrl() {
    return config.transformUrl || scriptAssetUrl('hiool-pca192-transform.json');
  }

  function resolvedChunkUrl(filename) {
    if (typeof config.chunkUrl === 'function') return config.chunkUrl(filename);
    const normalized = /^hiool-pca192-/.test(filename) ? filename : `hiool-pca192-${filename}`;
    return scriptAssetUrl(normalized);
  }

  function l2(values) {
    let sum = 0;
    for (let i = 0; i < values.length; i++) sum += values[i] * values[i];
    const denom = Math.sqrt(sum) || 1;
    const out = new Float32Array(values.length);
    for (let i = 0; i < values.length; i++) out[i] = values[i] / denom;
    return out;
  }

  function decodeInt8Base64(text) {
    const raw = atob(text);
    const out = new Int8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      const v = raw.charCodeAt(i);
      out[i] = v > 127 ? v - 256 : v;
    }
    return out;
  }

  function decodeFloat32Base64(text) {
    const raw = atob(text);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    return new Float32Array(bytes.buffer);
  }

  function dotFloatInt8(query, encoded) {
    let sum = 0;
    const n = Math.min(query.length, encoded.length);
    for (let i = 0; i < n; i++) sum += query[i] * (encoded[i] / 127);
    return sum;
  }

  async function fetchJson(url) {
    if (!url) throw new Error('Visual search asset URL could not be resolved.');
    const res = await fetch(url, { cache: 'force-cache', credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Visual search asset failed: ${res.status} ${url}`);
    return res.json();
  }

  async function canUseWebGPU() {
    if (!navigator.gpu?.requestAdapter) {
      runtime.webgpuAdapterAvailable = false;
      return false;
    }
    try {
      const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
      runtime.webgpuAdapterAvailable = Boolean(adapter);
      return Boolean(adapter);
    } catch {
      runtime.webgpuAdapterAvailable = false;
      return false;
    }
  }

  async function ensureHf() {
    if (!hfModulePromise) {
      hfModulePromise = (async () => {
        const hf = await import(config.transformersModule);
        if (hf.env) {
          hf.env.allowRemoteModels = true;
          const wasm = hf.env?.backends?.onnx?.wasm;
          if (wasm) {
            wasm.wasmPaths = config.wasmPath;
            wasm.numThreads = 1;
            if ('simd' in wasm) wasm.simd = true;
            runtime.wasmConfigured = true;
            runtime.wasmPath = config.wasmPath;
          }
        }
        return hf;
      })();
    }
    return hfModulePromise;
  }

  async function ensureDinoExtractor() {
    if (!dinoExtractorPromise) {
      dinoExtractorPromise = (async () => {
        const { pipeline } = await ensureHf();
        runtime.modelReady = false;
        runtime.lastModelError = null;

        if (config.preferWebGPU && await canUseWebGPU()) {
          try {
            const extractor = await pipeline('image-feature-extraction', config.dinoModel, {
              dtype: 'q8',
              device: 'webgpu',
            });
            runtime.backend = 'webgpu';
            runtime.modelReady = true;
            return extractor;
          } catch (error) {
            runtime.lastModelError = String(error?.message || error);
            console.warn('[HIOOL visual] optional WebGPU initialization failed; using WASM baseline.', error);
          }
        }

        try {
          const extractor = await pipeline('image-feature-extraction', config.dinoModel, {
            dtype: 'q8',
            device: 'wasm',
          });
          runtime.backend = 'wasm';
          runtime.modelReady = true;
          runtime.lastModelError = null;
          return extractor;
        } catch (error) {
          runtime.backend = null;
          runtime.modelReady = false;
          runtime.lastModelError = String(error?.message || error);
          throw error;
        }
      })();
    }
    return dinoExtractorPromise;
  }

  async function testWasmCdn() {
    const url = `${config.wasmPath}ort-wasm-simd-threaded.mjs`;
    try {
      const response = await fetch(url, { cache: 'no-store', mode: 'cors' });
      return {
        ok: response.ok,
        status: response.status,
        url,
        contentType: response.headers.get('content-type'),
      };
    } catch (error) {
      return { ok: false, status: 0, url, error: String(error?.message || error) };
    }
  }

  async function toObjectUrl(source) {
    if (source instanceof Blob) return { url: URL.createObjectURL(source), revoke: true };
    if (source instanceof HTMLCanvasElement) {
      const blob = await new Promise((resolve, reject) => source.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/jpeg', 0.94));
      return { url: URL.createObjectURL(blob), revoke: true };
    }
    if (typeof source === 'string') return { url: source, revoke: false };
    throw new Error('Unsupported visual search image source.');
  }

  function globalEmbedding(output) {
    const data = output?.data || output;
    const dims = output?.dims || [];
    if (!data?.length) throw new Error('DINOv3 returned no features.');
    const hidden = dims.length >= 3 ? dims[dims.length - 1] : data.length;
    if (!hidden || hidden > data.length) throw new Error(`Unexpected DINO embedding shape: ${JSON.stringify(dims)}`);
    return l2(Float32Array.from(data.slice(0, hidden)));
  }

  async function embedDino(source) {
    const { RawImage } = await ensureHf();
    const extractor = await ensureDinoExtractor();
    const ref = await toObjectUrl(source);
    try {
      const image = await RawImage.fromURL(ref.url);
      const output = await extractor(image);
      return globalEmbedding(output);
    } finally {
      if (ref.revoke) URL.revokeObjectURL(ref.url);
    }
  }

  async function ensureTransform() {
    if (!transformPromise) {
      transformPromise = (async () => {
        const url = resolvedTransformUrl();
        if (!url) throw new Error('PCA transform URL is not configured and could not be auto-resolved.');
        const raw = await fetchJson(url);
        const mean = decodeFloat32Base64(raw.meanF32Base64);
        const components = decodeFloat32Base64(raw.componentsF32Base64);
        const scale = decodeFloat32Base64(raw.scaleF32Base64);
        const rows = raw.componentsShape?.[0] || raw.outputDimension || 192;
        const cols = raw.componentsShape?.[1] || raw.sourceDimension || 384;
        if (mean.length !== cols) throw new Error(`Invalid PCA mean length: ${mean.length}, expected ${cols}`);
        if (scale.length !== rows) throw new Error(`Invalid PCA scale length: ${scale.length}, expected ${rows}`);
        if (components.length !== rows * cols) throw new Error(`Invalid PCA components length: ${components.length}, expected ${rows * cols}`);
        return { raw, mean, components, scale, rows, cols, url };
      })();
    }
    return transformPromise;
  }

  async function projectPca(query) {
    const t = await ensureTransform();
    if (query.length !== t.cols) throw new Error(`Unexpected DINO feature size: ${query.length}; expected ${t.cols}`);
    const out = new Float32Array(t.rows);
    for (let r = 0; r < t.rows; r++) {
      let sum = 0;
      const offset = r * t.cols;
      for (let c = 0; c < t.cols; c++) sum += (query[c] - t.mean[c]) * t.components[offset + c];
      out[r] = sum / (t.scale[r] || 1);
    }
    return l2(out);
  }

  async function ensureCatalog() {
    if (!catalogPromise) {
      catalogPromise = (async () => {
        const manifestUrl = resolvedManifestUrl();
        if (!manifestUrl) throw new Error('PCA manifest URL is not configured and could not be auto-resolved.');
        const manifest = await fetchJson(manifestUrl);
        const chunkUrls = manifest.chunks.map((chunk) => resolvedChunkUrl(chunk.file));
        if (chunkUrls.some((url) => !url)) throw new Error('One or more PCA chunk URLs could not be resolved.');
        const chunks = await Promise.all(chunkUrls.map(fetchJson));
        const products = [];
        for (const chunk of chunks) {
          for (const p of chunk.products || []) {
            products.push({
              id: p.id,
              title: p.title,
              handle: p.handle,
              url: p.url,
              image: p.image,
              vectors: (p.vectors || []).map(decodeInt8Base64),
              centroid: p.centroid ? decodeInt8Base64(p.centroid) : null,
            });
          }
        }
        if (!products.length) throw new Error('PCA catalog loaded but contained no products.');
        return { manifest, products, manifestUrl, chunkUrls };
      })();
    }
    return catalogPromise;
  }

  function scoreCatalog(query, catalog) {
    const maxWeight = Number(config.maxWeight ?? 0.7);
    const centroidWeight = Number(config.centroidWeight ?? 0.3);
    const ranked = catalog.products.map((product) => {
      let best = -Infinity;
      for (const vector of product.vectors) best = Math.max(best, dotFloatInt8(query, vector));
      const centroidScore = product.centroid ? dotFloatInt8(query, product.centroid) : best;
      const score = maxWeight * best + centroidWeight * centroidScore;
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        url: product.url,
        image: product.image,
        score,
        maxScore: best,
        centroidScore,
      };
    });
    ranked.sort((a, b) => b.score - a.score);
    return ranked;
  }

  async function selfTest() {
    try {
      stage('self-test-assets');
      const [catalog, transform] = await Promise.all([ensureCatalog(), ensureTransform()]);
      const expectedProducts = catalog.manifest?.sourceProductCount || 0;
      return {
        ok: true,
        products: catalog.products.length,
        expectedProducts,
        chunks: catalog.manifest?.chunks?.length || 0,
        transformRows: transform.rows,
        transformCols: transform.cols,
        manifestUrl: catalog.manifestUrl,
        transformUrl: transform.url,
        scriptUrl: EXECUTING_SCRIPT_SRC,
        wasmPath: config.wasmPath,
      };
    } catch (error) {
      lastError = error;
      return { ok: false, stage: lastStage, error: String(error?.message || error), scriptUrl: EXECUTING_SCRIPT_SRC };
    }
  }

  async function preload(options = {}) {
    try {
      lastError = null;
      stage('preload-assets', options.onStage);
      await Promise.all([ensureCatalog(), ensureTransform()]);
      stage('preload-transformers', options.onStage);
      await ensureHf();
      stage('preload-model', options.onStage);
      await ensureDinoExtractor();
      stage('ready', options.onStage);
      return true;
    } catch (error) {
      lastError = error;
      console.error('[HIOOL visual] preload failed', error);
      throw error;
    }
  }

  async function search(source, options = {}) {
    const topK = options.topK || config.topK;
    lastError = null;
    try {
      stage('dino-loading', options.onStage);
      const [rawQuery, catalog] = await Promise.all([embedDino(source), ensureCatalog()]);
      stage('pca-project', options.onStage);
      const query = await projectPca(rawQuery);
      stage('catalog-search', options.onStage);
      const rows = scoreCatalog(query, catalog);
      const margin = rows.length > 1 ? rows[0].score - rows[1].score : 1;
      stage('complete', options.onStage);
      return {
        mode: 'dino-pca192',
        strategy: 'pca192-whiten:blend',
        backend: runtime.backend,
        margin,
        results: rows.slice(0, topK),
      };
    } catch (error) {
      lastError = error;
      console.error('[HIOOL visual] search failed', { stage: lastStage, error });
      throw error;
    }
  }

  function configure(next) {
    config = { ...config, ...next };
    runtime.wasmPath = config.wasmPath;
    catalogPromise = null;
    transformPromise = null;
    hfModulePromise = null;
    dinoExtractorPromise = null;
    runtime.backend = null;
    runtime.modelReady = false;
    runtime.lastModelError = null;
    return { ...config };
  }

  window.HIOOLVisualSearchPCA192 = {
    configure,
    preload,
    selfTest,
    search,
    testWasmCdn,
    canUseWebGPU,
    getConfig: () => ({
      ...config,
      resolvedManifestUrl: resolvedManifestUrl(),
      resolvedTransformUrl: resolvedTransformUrl(),
      scriptUrl: EXECUTING_SCRIPT_SRC,
    }),
    getDebugState: () => ({
      stage: lastStage,
      error: lastError ? String(lastError?.message || lastError) : null,
      scriptUrl: EXECUTING_SCRIPT_SRC,
      backend: runtime.backend,
      webgpuAvailable: runtime.webgpuAvailable,
      webgpuAdapterAvailable: runtime.webgpuAdapterAvailable,
      wasmConfigured: runtime.wasmConfigured,
      wasmPath: runtime.wasmPath,
      modelReady: runtime.modelReady,
      lastModelError: runtime.lastModelError,
    }),
  };
})();

